<script setup lang="ts">
/**
 * A password input with a show/hide toggle — one drawing, used by both the
 * join screen and the reset-password screen, so the eye icon and its
 * behaviour cannot drift between the two places a player types a password
 * into this app.
 *
 * The icon is drawn in SVG rather than fetched, the same rule every other
 * small UI glyph in this codebase follows (the radar bezel, the title
 * badges, the key caps, the currency marks): it renders at
 * `devicePixelRatio`, where a fixed raster would be soft on half the
 * machines running the game.
 */
withDefaults(
  defineProps<{
    modelValue: string;
    id: string;
    label: string;
    autocomplete?: string;
    placeholder?: string;
    disabled?: boolean;
    dataTest?: string;
  }>(),
  { autocomplete: 'off', placeholder: '', disabled: false, dataTest: undefined },
);

const emit = defineEmits<{ 'update:modelValue': [string] }>();

import { ref } from 'vue';

const visible = ref(false);

function onInput(event: Event): void {
  emit('update:modelValue', (event.target as HTMLInputElement).value);
}
</script>

<template>
  <div class="password-field">
    <label :for="id">{{ label }}</label>
    <div class="password-field-row">
      <input
        :id="id"
        :type="visible ? 'text' : 'password'"
        :value="modelValue"
        :autocomplete="autocomplete"
        :placeholder="placeholder"
        :disabled="disabled"
        :data-test="dataTest"
        @input="onInput"
      />
      <button
        type="button"
        class="password-toggle"
        :aria-label="visible ? 'Hide password' : 'Show password'"
        :aria-pressed="visible"
        :disabled="disabled"
        @click="visible = !visible"
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linejoin="round"
          />
          <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.6" />
          <line
            v-if="!visible"
            x1="3.5"
            y1="20.5"
            x2="20.5"
            y2="3.5"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
.password-field {
  display: contents;
}

label {
  margin-top: 0.35rem;
  font-size: 0.65rem;
  line-height: 1rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.password-field-row {
  position: relative;
  display: flex;
}

input {
  flex: 1;
  min-width: 0;
  padding: 0.4rem 2.6rem 0.4rem 0.85rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: var(--steel-900);
  color: #fff;
  font: inherit;
}

/* Dimmer than the typed value, deliberately — a placeholder at the same
   contrast as real text reads as an answer already filled in. */
input::placeholder {
  color: var(--steel-400);
  opacity: 0.7;
}

input:focus {
  outline: none;
  border-color: var(--accent);
}

.password-toggle {
  position: absolute;
  top: 0;
  right: 0;
  display: grid;
  place-items: center;
  width: 2.6rem;
  height: 100%;
  min-height: 44px;
  border: none;
  background: none;
  color: var(--steel-400);
  cursor: pointer;
}

.password-toggle:hover {
  color: #fff;
}

.password-toggle:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.password-toggle svg {
  width: 1.15rem;
  height: 1.15rem;
}
</style>
